import { useTranslation } from 'react-i18next'
import EmojioneV1Fireworks from '~icons/emojione-v1/fireworks'
import { dialog } from 'src/components/Modal/DynamicModal'
import type {
  RankingContentTypeValues,
  RankingRangeTypeValues,
} from 'src/components/Ranking/ranking-utils'
import type { Artwork } from 'src/services/apollo/generated/graphql'
import { isAnimatedArtwork } from 'src/utils/artwork'
import { cx } from 'src/utils/cx'
import { useIsMobile } from 'src/utils/hooks/useBreakpoint'
import { getMediaUrl, getPublicUrl } from 'src/utils/media'
import ArtworkSocialShareButtons from '../ArtworkDetail/ArtworkShare/ArtworkSocialShareBtns'
import { VideoMedia } from '../Media/VideoMedia'
import { RankingBottomActionBar } from './RankingBottomActionBar'
import { RankingOverlay } from './RankingOverlay'

export type RankingArtworkShareProps = {
  artwork: Artwork
  opts: {
    filterType: RankingRangeTypeValues
    type: RankingContentTypeValues
    rankNumber: number
  }
  onClose?: () => void
}

export const openShareRankingArtworkDialog = (
  artwork: Artwork,
  opts: RankingArtworkShareProps['opts'],
) => {
  return new Promise<boolean | null>((resolve, reject) => {
    const instanceRef = dialog({
      children: (
        <RankingArtworkShareDialogContent
          onClose={() => {
            instanceRef.current?.close()
            resolve(null)
          }}
          artwork={artwork}
          opts={opts}
        />
      ),
      onClose: () => {
        resolve(null)
      },
      fullWidth: false,
      responsive: true,
      dialogProps: {
        maxWidth: 'sm',
      },
    })
  })
}

function RankingArtworkShareDialogContent(props: RankingArtworkShareProps) {
  const { t } = useTranslation(['common'])

  return (
    <>
      <section className="flex flex-col items-center pt-6 px-6">
        <EmojioneV1Fireworks width={48} height={48} />
        <h1 className="mt-4 text-center font-semibold text-lg sm:text-xl">
          {t('common:ranking.title.share-dialog.top-congrats')}
        </h1>
        <p className="whitespace-pre-line text-center font-semibold text-lg sm:text-xl">
          {t('common:ranking.title.share-dialog.top', {
            filterType: t(
              `common:ranking.filter-type.${props.opts.filterType}`,
            ),
            type: t(`common:ranking.${props.opts.type}.label`),
          })}
        </p>
      </section>
      <section>
        <RankingArtworkDialogContent {...props} />
      </section>
    </>
  )
}

export default function RankingArtworkDialogContent(
  props: RankingArtworkShareProps,
) {
  const { artwork, opts } = props

  if (!artwork.media) return null

  return (
    <>
      <RankingArtworkShareActionBar artwork={artwork} />
      <RankingArtworkPreview artwork={artwork} rankingOpts={opts} />
    </>
  )
}

function RankingArtworkShareActionBar(props: { artwork: Artwork }) {
  return (
    <div className="py-4 w-full overflow-x-auto">
      <div
        className={
          'flex items-center justify-center gap-2 w-fit mx-auto px-6 [&>a]:!size-9 [&>a]:!shrink-0 [&>button]:!size-9 [&>button]:!shrink-0'
        }
      >
        <ArtworkSocialShareButtons artwork={props.artwork} scene="ranking" />
      </div>
    </div>
  )
}

const displayHeightDeduction = 32 * 2 + 136 + 48 + 58 + 16 * 2 + 64
const mobileDisplayHeightDeduction = 64 + 24 + 128 + 48 + 58 + 16 * 2 + 64

function RankingArtworkPreview(props: {
  artwork: Artwork
  rankingOpts: RankingArtworkShareProps['opts']
}) {
  const isMobile = useIsMobile()

  const { artwork, rankingOpts } = props
  const { media } = artwork
  if (!media?.height) return null

  const visibleHeight =
    document.documentElement.style.getPropertyValue('--visible-height')

  const displayHeight = Math.max(
    300,
    (Number(visibleHeight.replace('px', '')) || 0) -
      (isMobile ? mobileDisplayHeightDeduction : displayHeightDeduction),
  )

  // Watermark's height should be scaled to about 1/12 of image height
  const watermarkHeight = media.height / 12

  // Watermark's rendered height is ~75px at 1x scale (6em * 12.5px base)
  const snapshotWatermarkScale = watermarkHeight / 75
  const previewScale = (displayHeight / media.height) * snapshotWatermarkScale

  const imageHeight = displayHeight

  return (
    <RankingArtworkWithWatermarkImage
      className="my-4 px-6"
      imageHeight={imageHeight}
      artwork={props.artwork}
      scale={previewScale}
      rankingOpts={rankingOpts}
    />
  )
}

function RankingArtworkWithWatermarkImage(
  props: {
    imageHeight?: number
    artwork: Artwork
    rankingOpts: RankingArtworkShareProps['opts']
    /**
     * fontSize em scale
     */
    scale?: number
  } & StyleProps,
) {
  const { t } = useTranslation(['common'])
  const { imageHeight, artwork, scale, rankingOpts } = props
  const { media } = artwork
  if (!media) return null
  const imageUrl = getPublicUrl(media)
  const videoUrl =
    isAnimatedArtwork(artwork) && getMediaUrl(artwork.videoMediaId, 'orig')

  return (
    <div
      className={cx(
        'flex flex-col items-center justify-center',
        props.className,
      )}
    >
      <div
        className="relative"
        style={{
          fontSize: `calc(16px * ${scale})`,
          paddingBottom: 64,
          // placeholder
          minHeight: imageHeight,
          minWidth:
            media.width && media.height && imageHeight
              ? (media.width / media.height) * imageHeight
              : 0,
        }}
      >
        {videoUrl ? (
          <VideoMedia
            autoPlay
            muted
            src={videoUrl}
            className="h-full object-cover rounded-lg"
            style={{ maxHeight: imageHeight }}
          />
        ) : (
          <img
            src={imageUrl}
            className="h-full object-cover rounded-lg"
            style={{ maxHeight: imageHeight }}
            loading="lazy"
            crossOrigin="anonymous"
          />
        )}
        <div
          className="absolute select-none top-0 left-0 w-full flex"
          style={{
            height: 'calc(100% - 64px)',
          }}
        >
          <RankingOverlay
            rankingOpts={rankingOpts}
            title={t('common:ranking.title.share-dialog.bottom', {
              filterType: t(
                `common:ranking.filter-type.${props.rankingOpts.filterType}`,
              ),
              type: t(`common:ranking.${props.rankingOpts.type}.label`),
            })}
            className="rounded-b-lg"
          />
        </div>
        <RankingBottomActionBar artwork={artwork} rankingOpts={rankingOpts} />
      </div>
    </div>
  )
}
